import { Spin } from 'antd'
import {
  LABEL_WIDTH,
  SectionContainer,
  TooltipDivider,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import {
  LABEL_JOINT_EFFICIENCY,
  LABEL_MAWP_DESIGN,
  LABEL_MAWP_R,
  LABEL_MAWP_RC,
  LABEL_MAWP_RL,
  LABEL_PRESSURE_COEFFICIENT,
  LABEL_QUALITY_FACTOR,
  LABEL_TSL,
} from 'features/indicationTriage/cards/ffsCalc/FfsFieldLabels'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { FfsLevel } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import {
  MawpCalcType,
  useFfsStrategy,
} from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import { FFS_DISPLAY_DECIMALS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import isNil from 'lodash/isNil'
import React, { Dispatch, FC, SetStateAction, useContext, useMemo } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { YieldStrengthParameterField } from './allowableStress/MetalAllowableStressTable'

const WORKING_UNIT_PRESS = GeckoMeasurementUnit.Megapascals

export interface FfsMawpSectionProps {
  mawp_c: GeckoMeasurement | null
  mawp_l: GeckoMeasurement | null
  mawpr: GeckoMeasurement | null
  mawpDesign: GeckoMeasurement | null
  failingMawpCriteria: boolean

  // Editables
  qualityFactor: GeckoMeasurement | null
  setQualityFactor?: Dispatch<SetStateAction<GeckoMeasurement | null>>
  qualityFactorCaption?: string
  pressureCoefficient: GeckoMeasurement | null
  setPressureCoefficient: Dispatch<SetStateAction<GeckoMeasurement | null>>
  pressureCoefficientCaption?: string
  tsl: GeckoMeasurement | null
  setTsl?: Dispatch<SetStateAction<GeckoMeasurement | null>>
  tslCaption?: string
  calcType?: MawpCalcType | null
  setCalcType?: Dispatch<SetStateAction<MawpCalcType | null>>
  loading: boolean

  // Configuration options
  addLongitudinalStress: boolean // Show longitudinal stress components (MAWP_RL, tsl)
  showMawpCriteria: boolean // Show MAWP criteria comparison
  ffsLevel: FfsLevel // FFS Level
}

export const LABEL_MAWP_CRITERIA = <>MAWP Criteria</>

const TOOLTIP_MAWP_DESIGN = <>Design pressure (MAWP) for the component</>

const TOOLTIP_QUALITY_FACTOR = (
  <>
    Joint Efficiency Factor
    <TooltipDivider />
    Quality factor for welded joints (typically 0.85-1.0)
  </>
)

const TOOLTIP_PRESSURE_COEFFICIENT = (
  <>
    Pressure Coefficient
    <TooltipDivider />
    Temperature-dependent coefficient (typically 0.4 for most cases)
  </>
)

const TOOLTIP_MAWP_CRITERIA = (
  <>
    MAWP Acceptability Criteria
    <TooltipDivider />
    Pass if {LABEL_MAWP_R} ≥ {LABEL_MAWP_DESIGN}
  </>
)

interface HookProps {
  t_c: GeckoMeasurement | null
  t_l: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  rsfa: GeckoMeasurement | null
  rsf: GeckoMeasurement | null
  designPressure: GeckoMeasurement | null
  ffsLevel: FfsLevel
  // Optional: PV-specific parameters
  tNom?: GeckoMeasurement | null
  calcType?: MawpCalcType | null
}

export function useFfsMawpProps({
  t_c,
  t_l,
  outerDiameter,
  rsfa,
  rsf,
  designPressure,
  ffsLevel,
  tNom,
  calcType,
}: HookProps): Omit<
  FfsMawpSectionProps,
  'loading' | 'ffsLevel' | 'addLongitudinalStress' | 'showMawpCriteria'
> {
  const {
    strategy: { mawp: mawpStrategy },
  } = useFfsStrategy()

  // E, Y and t_sl live in FfsSharedValues (shared with P, T and S under one
  // asset-model-snapshot prefill + override model) rather than local state
  // here, so all six MAWP operating params share a single provider.
  const {
    yieldStrength,
    qualityFactor,
    setQualityFactor,
    qualityFactorCaption,
    pressureCoefficient,
    setPressureCoefficient,
    pressureCoefficientCaption,
    weldStrengthReduction,
    tsl,
    setTsl,
    tslCaption,
  } = useContext(FfsSharedValuesContext)

  const mawp_c = useMemo<GeckoMeasurement | null>(() => {
    if (
      isNil(t_c) ||
      isNil(outerDiameter) ||
      isNil(qualityFactor) ||
      isNil(yieldStrength) ||
      isNil(mawpStrategy?.computeMawp)
    ) {
      return null
    }
    return mawpStrategy.computeMawp({
      t: t_c,
      tsl,
      yieldStrength,
      qualityFactor,
      outerDiameter,
      pressureCoefficient,
      weldStrengthReduction,
      tNom,
      calcType,
      axis: 'x',
    })
  }, [
    mawpStrategy,
    pressureCoefficient,
    weldStrengthReduction,
    outerDiameter,
    qualityFactor,
    t_c,
    yieldStrength,
    tsl,
    tNom,
    calcType,
  ])

  const mawp_l = useMemo<GeckoMeasurement | null>(() => {
    if (
      isNil(t_l) ||
      isNil(outerDiameter) ||
      isNil(qualityFactor) ||
      isNil(yieldStrength) ||
      isNil(mawpStrategy?.computeMawp)
    ) {
      return null
    }
    return mawpStrategy.computeMawp({
      t: t_l,
      tsl,
      yieldStrength,
      qualityFactor,
      outerDiameter,
      pressureCoefficient,
      weldStrengthReduction,
      tNom,
      calcType,
      axis: 'y',
    })
  }, [
    mawpStrategy,
    pressureCoefficient,
    weldStrengthReduction,
    outerDiameter,
    qualityFactor,
    t_l,
    yieldStrength,
    tsl,
    tNom,
    calcType,
  ])

  const mawpr = useMemo<GeckoMeasurement | null>(() => {
    if (isNil(mawpStrategy?.computeReratedMawp)) {
      return null
    }
    return mawpStrategy.computeReratedMawp({
      mawp_c,
      mawp_l,
      rsf,
      rsfa,
      ffsLevel,
    })
  }, [mawpStrategy, rsf, rsfa, ffsLevel, mawp_c, mawp_l])

  const failingMawpCriteria = useMemo(() => {
    if (isNil(mawpr) || isNil(designPressure)) {
      return false
    }

    return mawpr.convertTo(designPressure.unit).num < designPressure.num
  }, [mawpr, designPressure])

  return useMemo(
    () => ({
      qualityFactor,
      setQualityFactor,
      qualityFactorCaption,
      pressureCoefficient,
      setPressureCoefficient,
      pressureCoefficientCaption,
      tsl,
      setTsl,
      tslCaption,
      calcType,
      mawp_c,
      mawp_l,
      mawpr,
      mawpDesign: designPressure,
      failingMawpCriteria,
    }),
    [
      qualityFactor,
      setQualityFactor,
      qualityFactorCaption,
      pressureCoefficient,
      setPressureCoefficient,
      pressureCoefficientCaption,
      tsl,
      setTsl,
      tslCaption,
      calcType,
      mawp_c,
      mawp_l,
      mawpr,
      designPressure,
      failingMawpCriteria,
    ]
  )
}

const RawFfsMawpSection: FC<FfsMawpSectionProps> = ({
  mawp_c,
  mawp_l,
  mawpr,
  mawpDesign,
  failingMawpCriteria,

  // Editables
  qualityFactor,
  setQualityFactor,
  qualityFactorCaption,
  pressureCoefficient,
  setPressureCoefficient,
  pressureCoefficientCaption,
  tsl,
  setTsl,
  tslCaption,
  loading,

  // Configuration options
  addLongitudinalStress,
  showMawpCriteria,
  ffsLevel,
}) => {
  const {
    weldStrengthReduction,
    setWeldStrengthReduction,
    yieldStrength,
    setYieldStrength,
    yieldStrengthCaption,
    allowableStressRecords,
  } = useContext(FfsSharedValuesContext)
  const {
    strategy: { mawp: mawpStrategy },
  } = useFfsStrategy()

  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterField
          label={LABEL_QUALITY_FACTOR}
          labelTooltip={TOOLTIP_QUALITY_FACTOR}
          labelWidth={LABEL_WIDTH}
          value={qualityFactor}
          setValue={setQualityFactor}
          caption={qualityFactorCaption}
          min={GeckoMeasurement.CreateFromValue(
            0.1,
            GeckoMeasurementUnit.Ratio
          )}
          max={GeckoMeasurement.CreateFromValue(2, GeckoMeasurementUnit.Ratio)}
          step={0.1}
          measurementType='ratio'
        />

        <FfsParameterField
          label={LABEL_PRESSURE_COEFFICIENT}
          labelTooltip={TOOLTIP_PRESSURE_COEFFICIENT}
          labelWidth={LABEL_WIDTH}
          value={pressureCoefficient}
          setValue={setPressureCoefficient}
          caption={pressureCoefficientCaption}
          min={GeckoMeasurement.CreateFromValue(
            0.1,
            GeckoMeasurementUnit.Ratio
          )}
          max={GeckoMeasurement.CreateFromValue(2, GeckoMeasurementUnit.Ratio)}
          step={0.1}
          measurementType='ratio'
        />

        <FfsParameterField
          label={LABEL_JOINT_EFFICIENCY}
          labelTooltip='Weld join strength reduction factor'
          value={weldStrengthReduction}
          setValue={setWeldStrengthReduction}
          min={GeckoMeasurement.CreateFromValue(
            0.1,
            GeckoMeasurementUnit.Ratio
          )}
          max={GeckoMeasurement.CreateFromValue(2, GeckoMeasurementUnit.Ratio)}
          step={0.1}
          measurementType='ratio'
        />

        <YieldStrengthParameterField
          records={allowableStressRecords}
          stressValue={yieldStrength}
          setStressValue={setYieldStrength}
          caption={yieldStrengthCaption}
          labelWidth={LABEL_WIDTH}
        />

        {addLongitudinalStress && (
          <FfsParameterField
            label={LABEL_TSL}
            labelTooltip='Required shell thickness for supplemental loads'
            labelWidth={LABEL_WIDTH}
            value={tsl || null}
            setValue={setTsl}
            caption={tslCaption}
            min={GeckoMeasurement.CreateFromValue(
              0,
              GeckoMeasurementUnit.Millimeters
            )}
            max={GeckoMeasurement.CreateFromValue(
              10,
              GeckoMeasurementUnit.Millimeters
            )}
            step={0.1}
            measurementType='thickness'
          />
        )}

        <FfsParameterField
          label={LABEL_MAWP_DESIGN}
          labelTooltip={TOOLTIP_MAWP_DESIGN}
          labelWidth={LABEL_WIDTH}
          value={mawpDesign}
          measurementType='pressure'
        />

        <FfsParameterField
          label={LABEL_MAWP_RC}
          labelTooltip={mawpStrategy.renderCircumferentialTooltip()}
          labelWidth={LABEL_WIDTH}
          value={mawp_c}
          measurementType='pressure'
        />

        {addLongitudinalStress && (
          <FfsParameterField
            label={LABEL_MAWP_RL}
            labelTooltip={mawpStrategy.renderLongitudinalTooltip()}
            labelWidth={LABEL_WIDTH}
            value={mawp_l}
            measurementType='pressure'
          />
        )}

        <FfsParameterField
          label={LABEL_MAWP_R}
          labelTooltip={mawpStrategy.renderReratedTooltip(ffsLevel)}
          value={mawpr}
          measurementType='pressure'
        />

        {showMawpCriteria && mawpr && (
          <FfsParameterField
            label={LABEL_MAWP_CRITERIA}
            labelTooltip={TOOLTIP_MAWP_CRITERIA}
            labelWidth={LABEL_WIDTH}
            value={mawpr}
            measurementType='pressure'
            isValid={!failingMawpCriteria}
            trailingText={(value) => {
              const designInValueUnit = mawpDesign?.convertTo(
                value?.unit ?? WORKING_UNIT_PRESS
              )
              return `> ${designInValueUnit?.num.toFixed(FFS_DISPLAY_DECIMALS) || 'N/A'}`
            }}
          />
        )}
      </SectionContainer>
    </Spin>
  )
}

RawFfsMawpSection.displayName = 'FfsMawpSection'
export const FfsMawpSection = React.memo(RawFfsMawpSection)
